import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Link } from "react-router-dom";
import { Alert, Card, CardBody, Spinner } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import TableContainer from "../../components/Common/TableContainer";
import { clearDocumentErrors, deleteDocument, getCompaniesAll, getDocuments, getMyDocuments } from "store/actions";
import ConfirmModal from "components/Common/ConfirmModal";
import { fileBadge, formatDate, formatSize, formatTime, truncate } from "helpers/helper_funcations";
import { Text } from "./TableCol";
import { get } from "helpers/api_helper";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const DocumentsTable = (props) => {
  const dispatch = useDispatch();
  const hasInteracted = useRef(false);
  const {
    message,
    loading,
    items: documentsData,
    deleting,
    deleteSuccess,
    pagination
  } = useSelector((state) => state.Documents || {})
  const { allCompaniesData } = useSelector(state => state.Company)
  const { user: loggedUser } = useSelector(state => state.Login)
  const isAdmin = [1, 2].includes(Number(loggedUser?.role))
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [company, setCompany] = useState("")
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [isMessage, setMessage] = useState("")
  const [copiedId, setCopiedId] = useState(null)

  const openConfirm = (id) => {
    setPendingDeleteId(id)
    setConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!pendingDeleteId) return
    dispatch(deleteDocument(props.router.navigate, getDocuments, pendingDeleteId))
  }

  const handleCancelDelete = () => {
    setConfirmOpen(false)
    setPendingDeleteId(null)
  }

  const onSearch = (value) => {
    hasInteracted.current = true;
    setPageIndex(0)
    setSearch(value)
  }

  const onCompanyChange = (value) => {
    hasInteracted.current = true;
    setPageIndex(0)
    setCompany(value || "")
  }

  const onPageChange = (nextPageIndex) => {
    hasInteracted.current = true;
    setPageIndex(nextPageIndex)
  }

  const onPageSizeChange = (nextSize) => {
    hasInteracted.current = true;
    setPageIndex(0)
    setPageSize(nextSize)
  }

  const onDateRangeChange = (dates = []) => {
    hasInteracted.current = true;
    setPageIndex(0);
    const [start, end] = dates || [];
    const startISO = start ? new Date(start).toISOString() : null;
    const endISO = end
      ? new Date(new Date(end).setHours(23, 59, 59, 999)).toISOString()
      : null;
    setDateRange({ startDate: startISO, endDate: endISO });
  };

  const buildShareUrl = (doc) => {
    const base = new URL(window.location.origin);

    const identifier = doc?.shareToken || doc?.id;

    if (!identifier) return base.toString();

    base.pathname = `/view-document/${identifier}`;
    return base.toString();
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch { }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  const handleShare = async (doc) => {
    const shareUrl = buildShareUrl(doc);
    const title = doc?.name || "Document";
    const text = "View document";

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return;
      } catch (e) {
        console.error(e)
      }
    }

    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      setCopiedId(doc.id);
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      window.prompt("Copy this link:", shareUrl);
    }
  }

  const handleDownload = async (doc) => {
    if (!doc || selectedDoc?.id === doc.id) return;
    setSelectedDoc(doc);

    let url = null;
    try {
      url = await fetchSecureUrl({ download: true, id: doc.id });
      if (!url) return;

      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileName || doc.name || "file";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();

      if (!isAdmin) {
        dispatch(getMyDocuments())
      }
    } catch (e) {
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        console.error("Download failed:", e);
      }
    } finally {
      setSelectedDoc(null);
    }
  };

  const fetchSecureUrl = async ({ download = true, id } = {}) => {
    if (!id) return null;
    try {
      const qs = download ? "?download=true" : "";
      const json = await get(`/documents/${id}/secure-url${qs}`);
      if (!json?.success) throw new Error(json?.message || "Failed to fetch document");
      setMessage?.(null);
      return json.data?.signedUrl || null;
    } catch (e) {
      setMessage?.(e.message || "Failed to load document");
      return null;
    }
  };

  useEffect(() => {
    if (isAdmin) {
      dispatch(getCompaniesAll({ all: true }));
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (![3, 4].includes(Number(loggedUser?.role))) {
      const params = {
        page: pageIndex + 1,
        limit: pageSize,
        search,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      if (company && UUID_RE.test(company)) params.companyId = company;
      dispatch(getDocuments(params));
    } else {
      dispatch(getMyDocuments({
        page: pageIndex + 1,
        limit: pageSize,
        search,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }))
    }
  }, [pageIndex, pageSize, search, company, dateRange.startDate, dateRange.endDate, dispatch, loggedUser?.role])

  useEffect(() => {
    if (!deleting && deleteSuccess && confirmOpen) {
      setConfirmOpen(false)
      setPendingDeleteId(null)
    }
  }, [deleting, deleteSuccess])

  useEffect(() => {
    const t = setTimeout(() =>
      dispatch(clearDocumentErrors()), 8000)
    return () => clearTimeout(t)
  }, [message])

  const companiesById = useMemo(() => {
    const m = new Map();
    allCompaniesData?.forEach((c) => m.set(c.id, c));
    return m;
  }, [allCompaniesData])

  const companiesList = useMemo(
    () => Array.from(companiesById.values()),
    [companiesById]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Resource Name",
        accessor: "name",
        disableFilters: true,
        Cell: ({ row }) => {
          const { name, mimeType, fileName } = row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              {fileBadge(mimeType, fileName)}
              <span className="fw-semibold">{name}</span>
              {row.original.showOnResources === false && (
                <span className="badge bg-light text-dark">Hidden</span>
              )}
            </div>
          );
        },
      },
      {
        Header: "File",
        accessor: "fileName",
        disableFilters: true,
        Cell: ({ row }) => {
          const { fileName, size } = row.original;
          return (
            <div>
              <div title={fileName}>{truncate(String(fileName), 25)}</div>
              <small className="text-muted">{formatSize(size)}</small>
            </div>
          );
        },
      },
      ...([3, 4].includes(Number(loggedUser?.role)) ? [] : [
          {
            Header: "Assigned To",
            accessor: (row) =>
              (row.companyIds || [])
                .map((id) => companiesById?.get(id)?.name ? row.companyIds.length : 0)
                .join(", "),
            disableFilters: true,
            filterable: false,
            sortType: "basic",
            Cell: ({ row }) => {
              const ids = row.original.companyIds || []
              const preview = ids.slice(0, 3)
              return (
                <div className="d-flex flex-wrap gap-1">
                  {ids?.length !== 0 && <span className="badge bg-light text-dark">{ids?.length}</span>}
                  {preview?.map((id) => {
                    const c = companiesById?.get(id);
                    return (
                      <span
                        key={id}
                        className="badge bg-secondary border"
                        title={c?.domain}
                      >
                        {c?.name || id}
                      </span>
                    );
                  })}
                  {ids.length === 0 && <span className="text-muted">—</span>}
                  {ids.length > 3 && (
                    <span className="badge bg-light text-dark">+{ids.length - 3} more</span>
                  )}
                </div>
              );
            },
          },

          {
            Header: "Viewed",
            accessor: "viewCount",
            disableFilters: true,
            Cell: ({ value }) => <span>{!value ? 0 : value}</span>,
          },
          {
            Header: "View Time",
            accessor: row =>
              [1, 2].includes(loggedUser?.role)
                ? row.totalReadSeconds
                : row.myReadSeconds,
            disableFilters: true,
            Cell: ({ value }) => <span>{formatTime(value)}</span>,
          },
          {
            Header: "Downloads",
            accessor: "downloadCount",
            disableFilters: true,
            Cell: ({ value }) => <span>{!value ? 0 : value}</span>,
          },
          {
            Header: "Created",
            accessor: "createdAt",
            disableFilters: true,
            Cell: ({ value }) => <span>{formatDate(value)}</span>,
          },
        ]),
      {
        Header: "Action",
        disableFilters: true,
        Cell: ({ row }) => {
          const id = row.original.id;
          const doc = row.original;
          return (
            <div className="flex-row">
              <Link
                className="download-rounded-btn"
                to={`/view-document/${id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="mdi mdi-eye" style={{ color: "white", fontSize: 15 }} />
              </Link>
              <button
                type="button"
                className="download-rounded-btn"
                onClick={() => handleDownload(doc)}
                disabled={selectedDoc?.id === id}
                title={selectedDoc?.id === id ? "Downloading…" : "Download"}
                style={{ border: "none" }}
              >
                {selectedDoc?.id === id ? (
                  <i className="mdi mdi-loading mdi-spin" style={{ color: "white", fontSize: 15 }} />
                ) : (
                  <i className="mdi mdi-download" style={{ color: "white", fontSize: 15 }} />
                )}
              </button>

              {/* <button
                type="button"
                className="download-rounded-btn"
                onClick={() => handleShare(doc)}
                title={copiedId === id ? "Copied!" : "Share"}
                style={{ border: "none" }}
              >
                <i
                  className={`mdi ${copiedId === id ? "mdi-check" : "mdi-share-variant"}`}
                  style={{ color: "white", fontSize: 15 }}
                />
              </button> */}
              {![3, 4].includes(Number(loggedUser?.role)) && (
                <>
                  <Link className="download-rounded-btn" to={`/resource/${id}`}>
                    <i className="mdi mdi-pencil" style={{ color: "white", fontSize: 15 }}></i>
                  </Link>
                  <button
                    type="button"
                    className="download-rounded-btn"
                    onClick={() => openConfirm(id)}
                    disabled={deleting}
                    title={deleting ? "Deleting…" : "Delete"}
                    style={{ border: "none" }}
                  >
                    <i className="mdi mdi-delete" style={{ color: "white", fontSize: 15 }} />
                  </button>
                </>
              )}
            </div>
          );
        },
      },
    ];

    return baseColumns;
  }, [companiesById, selectedDoc?.id]);

  const isDistributorView = [3, 4].includes(Number(loggedUser?.role))

  const documentsSorted = useMemo(
    () => {
      const docs = Array.isArray(documentsData) ? [...documentsData] : []
      if (isDistributorView) {
        return docs.sort((a, b) =>
          String(a.name || a.fileName || "").localeCompare(
            String(b.name || b.fileName || ""),
            undefined,
            { sensitivity: "base", numeric: true }
          )
        )
      }
      return docs.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )
    },
    [documentsData, isDistributorView]
  );

  const ROLE_LABELS = {
    1: "Super Admin",
    2: "Admin"
  };
  const roleLabel = ROLE_LABELS[loggedUser?.role] ?? "Admin";

  return (
    <>
      <div className="row-spaceBtwn align-items-center mb-2">
        <div className="h2 mb-0 heading">Resources</div>
        {/* <div onClick={() => loggedUser?.role !== 3 ? dispatch(getDocuments()) : dispatch(getMyDocuments())}>
          {!loading ? (
            <img
              src={require("../../assets/images/refresh.png")}
              height={23}
              className="cursor-pointer"
              style={{ cursor: "pointer" }}
              alt="refresh"
            />
          ) : (
            <img
              src={require("../../assets/images/refresh.gif")}
              height={23}
              className="cursor-pointer"
              style={{ cursor: "pointer" }}
              alt="refreshing"
            />
          )}
        </div> */}
      </div >

      <Card className="shadow-none mb-0">
        <CardBody style={{ background: "#F8F8FB" }} className="pb-0 px-0 px-sm-4">
          {!!message || isMessage && <Alert color="danger" className="mb-4">
            {String(message || isMessage)}
          </Alert>}
          {isAdmin && <p>Count will not work becuase you are logged in as {roleLabel}.</p>}
          <TableContainer
            columns={columns}
            data={documentsSorted}
            isGlobalFilter={true}
            isJobListGlobalFilter={true}
            isAddOptions={false}
            isPagination={true}
            iscustomPageSizeOptions={false}
            customPageSize={10}
            totalPages={pagination?.totalPages}
            manual
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSearch={onSearch}
            searchValue={search}
            onFilterChange={onCompanyChange}
            filterValue={company}
            filterData={companiesList}
            selectTitle={isAdmin ? "Select Company" : ""}
            loading={loading}
            onDateRangeChange={onDateRangeChange}
            dateRangeValue={dateRange}
            pagination="pagination pagination-rounded justify-content-end mb-2"
          />
        </CardBody>
        <ConfirmModal
          isOpen={confirmOpen}
          title="Confirm Delete"
          confirmLabel="Delete"
          confirmColor="danger"
          loading={deleting}
          errorMessage={message || ""}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        >
          Are you sure you want to delete this document? This action cannot be undone.
        </ConfirmModal>
      </Card>
    </>
  );
};

DocumentsTable.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(DocumentsTable);
